/**
 * Automations & Executions API Client
 *
 * Wrapper functions for all automation and execution endpoints.
 * Uses the shared KodeonApiClient for authenticated HTTP requests.
 *
 * The backend operations service uses a flat schema (trigger_event_type,
 * enabled, unix timestamps) while the frontend uses a nested schema
 * (trigger: { type, config }, status, ISO strings). This module contains
 * a mapping layer that reconciles the two.
 *
 * @see Task 2564 Section 10 for backend API surface
 */

import { api } from '@/lib/api'
import type {
  Automation,
  AutomationListResponse,
  AutomationUpdateRequest,
  CreateAutomationRequest,
  AutomationFilters,
  AutomationSortState,
  AutomationStatus,
  AutomationTriggerType,
  Execution,
  ExecutionListResponse,
  ExecutionStatus,
  ExecutionStep,
  ExecutionStepStatus,
  RunAutomationResponse,
  ExecutionFilters,
} from './types'

// ── API Paths ───────────────────────────────────────────────────────────────

const PATHS = {
  automations: '/v1/automations',
  automation: (id: string) => `/v1/automations/${id}`,
  execute: (id: string) => `/v1/automations/${id}/execute`,
  automationExecutions: (id: string) => `/v1/automations/${id}/executions`,
  executions: '/v1/executions',
  execution: (id: string) => `/v1/executions/${id}`,
  executionApprove: (id: string) => `/v1/executions/${id}/approve`,
  executionReject: (id: string) => `/v1/executions/${id}/reject`,
  executionRetry: (id: string) => `/v1/executions/${id}/retry`,
} as const

// ── Backend ↔ Frontend Mapping ──────────────────────────────────────────────

/**
 * Raw automation shape returned by the backend operations service.
 * Uses flat trigger fields and an `enabled` boolean instead of
 * the frontend's nested `trigger` object and `status` string.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type BackendAutomation = Record<string, any>

/**
 * Maps a raw backend automation response to the frontend Automation type.
 *
 * Backend → Frontend field mapping:
 *   trigger_event_type  → trigger.type = 'event',  trigger.config.event_type
 *   trigger_schedule    → trigger.type = 'cron',   trigger.config.schedule
 *   enabled: true       → status: 'active'
 *   enabled: false      → status: 'paused'
 *   created (unix)      → createdAt (ISO string)
 *   updated (unix)      → updatedAt (ISO string)
 *   steps[].id          → steps[].index (positional)
 */
function mapAutomationFromBackend(raw: BackendAutomation): Automation {
  // Determine trigger type from flat fields.
  let triggerType: AutomationTriggerType = 'event'
  const triggerConfig: Record<string, unknown> = {}

  if (raw.trigger_schedule) {
    triggerType = 'cron'
    triggerConfig.schedule = raw.trigger_schedule
  } else if (raw.trigger_event_type) {
    triggerType = 'event'
    triggerConfig.event_type = raw.trigger_event_type
  }

  // If the backend already returns a nested trigger object (future-proofing),
  // prefer it over the flat fields.
  const trigger = raw.trigger?.type
    ? raw.trigger
    : { type: triggerType, config: triggerConfig }

  // Map enabled boolean + deprecated flag → status string.
  // Deprecated takes precedence over paused — they are visually distinct states.
  let status: AutomationStatus = 'active'
  if (raw.deprecated === true) {
    status = 'deprecated'
  } else if (raw.status && typeof raw.status === 'string') {
    // Backend already has a status string (future-proofing).
    status = raw.status as AutomationStatus
  } else if (typeof raw.enabled === 'boolean') {
    status = raw.enabled ? 'active' : 'paused'
  }

  // Map unix timestamps → ISO strings.
  const createdAt =
    raw.createdAt ?? (raw.created ? new Date(raw.created * 1000).toISOString() : new Date().toISOString())
  const updatedAt =
    raw.updatedAt ?? (raw.updated ? new Date(raw.updated * 1000).toISOString() : createdAt)

  // Map steps — backend uses { id, name, type, config }, frontend uses { index, name, type, config }.
  const steps = Array.isArray(raw.steps)
    ? raw.steps.map((s: Record<string, unknown>, i: number) => ({
        index: i,
        name: (s.name as string) ?? `Step ${i + 1}`,
        type: (s.type as string) ?? 'unknown',
        config: (s.config as Record<string, unknown>) ?? {},
      }))
    : []

  return {
    id: raw.id,
    name: raw.name ?? '',
    description: raw.description,
    status,
    trigger,
    steps,
    inputSchema: raw.input_schema ?? raw.inputSchema,
    estimatedRuntimeMs: raw.estimated_runtime_ms ?? raw.estimatedRuntimeMs,
    lastRunAt: raw.last_run_at ?? raw.lastRunAt,
    successRate7d: raw.success_rate_7d ?? raw.successRate7d,
    createdAt,
    updatedAt,
    // Deprecation fields
    deprecated: raw.deprecated ?? false,
    deprecatedReason: raw.deprecated_reason,
    deprecatedAt: raw.deprecated_at
      ? typeof raw.deprecated_at === 'number'
        ? new Date(raw.deprecated_at * 1000).toISOString()
        : String(raw.deprecated_at)
      : undefined,
    // Summariser fields
    summariserHint: raw.summariser_hint,
    summariseExecution: raw.summarise_execution,
    // Preserve raw backend data for edit form pre-population
    _raw: raw,
  }
}

// ── Execution Mapping ───────────────────────────────────────────────────────

/**
 * Raw execution shape returned by the operations service.
 *
 * Mirrors ``services/operations/backend/internal/core/domain/automation_execution.go``
 * — snake_case keys with unix int64 timestamps.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type BackendExecution = Record<string, any>

/**
 * Backend execution statuses that count as terminal.  Used to decide whether
 * to back-fill ``completedAt`` from ``updated`` for executions persisted
 * before the backend exposed ``completed_at`` (any execution older than the
 * domain change that added the field).
 */
const TERMINAL_BACKEND_STATUSES = new Set([
  'success',
  'failed',
  'cancelled',
  'skipped',
  'superseded',
])

/**
 * Convert a backend execution status to the frontend vocabulary.
 *
 * Backend (``AutomationExecutionStatus``)            → Frontend (``ExecutionStatus``)
 *   running                                          → running
 *   pending_agent_review (async agent in-flight)     → running
 *   success                                          → succeeded
 *   failed                                           → failed
 *   suspended                                        → awaiting_approval
 *   pending_operator_review                          → awaiting_approval
 *   cancelled                                        → aborted
 *   skipped                                          → aborted
 *   superseded                                       → aborted
 *
 * Backend has more states than the frontend renders.  Several collapse to
 * ``aborted`` (operator never sees them as a distinct outcome) and the two
 * pending-review states collapse to ``awaiting_approval``.  Anything we
 * don't recognise falls through to ``pending`` so the row at least renders.
 */
function mapExecutionStatusFromBackend(raw: unknown): ExecutionStatus {
  switch (raw) {
    case 'running':
    case 'pending_agent_review':
      return 'running'
    case 'success':
      return 'succeeded'
    case 'failed':
      return 'failed'
    case 'suspended':
    case 'pending_operator_review':
      return 'awaiting_approval'
    case 'cancelled':
    case 'skipped':
    case 'superseded':
      return 'aborted'
    // Pass-through for values that already use frontend vocabulary
    // (defensive — covers the case where some adapter has already mapped).
    case 'succeeded':
    case 'aborted':
    case 'awaiting_approval':
    case 'pending':
      return raw
    default:
      return 'pending'
  }
}

/**
 * Convert a backend step status to the frontend ``ExecutionStepStatus``.
 *
 *   completed → succeeded
 *   running / pending / failed / skipped → identity
 *
 * The backend (``automation_step_execution.go``) emits ``completed`` for a
 * step that finished successfully — the frontend renders ``succeeded``.
 */
function mapStepStatusFromBackend(raw: unknown): ExecutionStepStatus {
  switch (raw) {
    case 'completed':
    case 'succeeded':
      return 'succeeded'
    case 'running':
    case 'pending':
    case 'failed':
    case 'skipped':
      return raw
    default:
      return 'pending'
  }
}

/**
 * Convert a Unix-epoch seconds integer to an ISO-8601 string.
 *
 * Returns ``undefined`` for any non-positive, non-finite, or non-number
 * input so callers can pass the raw backend field straight through.  The
 * Runs tab and ExecutionLogPane both guard against ``undefined`` to avoid
 * the ``RangeError: Invalid time value`` crash.
 */
function unixToIso(value: unknown): string | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    return undefined
  }
  return new Date(value * 1000).toISOString()
}

/**
 * Map a raw backend step-execution record to the frontend ``ExecutionStep``.
 *
 * Backend (``AutomationStepExecution``)  → Frontend (``ExecutionStep``)
 *   step_id (string)                     → name (display label)
 *   status (string)                      → status (mapped via mapStepStatusFromBackend)
 *   started_at (unix int64)              → startedAt (ISO 8601)
 *   completed_at (unix int64)            → completedAt (ISO 8601)
 *   input / output                       → identity (unknown payloads)
 *   error (string)                       → { message: error } when present
 *
 * The frontend uses a positional ``index`` for step ordering — supplied by
 * the caller because the backend has no equivalent (steps are ordered by
 * their position in the StepExecutions slice).
 */
function mapStepExecutionFromBackend(
  raw: Record<string, unknown>,
  index: number
): ExecutionStep {
  const startedAt = unixToIso(raw.started_at)
  const completedAt = unixToIso(raw.completed_at)

  // Derive a duration when both endpoints are present — useful for the
  // step-card timing label even though the backend doesn't compute it.
  const durationMs =
    startedAt && completedAt
      ? new Date(completedAt).getTime() - new Date(startedAt).getTime()
      : typeof raw.duration_ms === 'number'
      ? raw.duration_ms
      : undefined

  const errMsg = typeof raw.error === 'string' && raw.error ? raw.error : undefined

  return {
    index,
    name:
      (typeof raw.step_id === 'string' && raw.step_id) ||
      (typeof raw.name === 'string' && raw.name) ||
      `Step ${index + 1}`,
    status: mapStepStatusFromBackend(raw.status),
    startedAt,
    completedAt,
    durationMs,
    cost: typeof raw.cost === 'number' ? raw.cost : undefined,
    input: raw.input,
    output: raw.output,
    error: errMsg ? { message: errMsg } : undefined,
  }
}

/**
 * Map a raw backend execution response to the frontend ``Execution`` type.
 *
 * Backend (``AutomationExecution``)      → Frontend (``Execution``)
 *   id                                   → id
 *   automation_id                        → automationId
 *   status (success/failed/…)            → status (succeeded/failed/…)  via mapExecutionStatusFromBackend
 *   started_at (unix int64)              → startedAt (ISO 8601)
 *   completed_at (unix int64)            → completedAt (ISO 8601)
 *   step_executions[]                    → steps[] via mapStepExecutionFromBackend
 *   trigger_payload                      → parameters
 *   (computed from steps)                → totalCost
 *
 * Back-compat for executions persisted before ``started_at``/``completed_at``
 * existed on the backend struct: fall back to ``APIResource.created``
 * (always present) for ``startedAt``, and to ``APIResource.updated`` for
 * ``completedAt`` *only* when the execution is in a terminal state.
 */
function mapExecutionFromBackend(raw: BackendExecution): Execution {
  const startedAt = unixToIso(raw.started_at) ?? unixToIso(raw.created)

  // ``queued_at`` (unix int64, omitted/zero when the execution never queued)
  // marks each time the run parked for operator review — used by the
  // "Awaiting Approval" wait timer.  ``unixToIso`` returns ``undefined`` for
  // a missing/zero value, so the timer's fallback to ``startedAt`` kicks in.
  const queuedAt = unixToIso(raw.queued_at)

  const isTerminal =
    typeof raw.status === 'string' && TERMINAL_BACKEND_STATUSES.has(raw.status)
  const completedAt =
    unixToIso(raw.completed_at) ??
    (isTerminal ? unixToIso(raw.updated) : undefined)

  const stepsRaw: unknown =
    (Array.isArray(raw.step_executions) && raw.step_executions) ||
    (Array.isArray(raw.steps) && raw.steps) ||
    []
  const steps = (stepsRaw as Record<string, unknown>[]).map((s, i) =>
    mapStepExecutionFromBackend(s, i)
  )

  // ``total_cost`` is not currently on the backend struct; ``calculateExecutionCost``
  // falls back to summing per-step cost when this field is missing.
  const totalCost =
    typeof raw.total_cost === 'number'
      ? raw.total_cost
      : typeof raw.totalCost === 'number'
      ? raw.totalCost
      : undefined

  return {
    id: String(raw.id ?? ''),
    automationId: String(raw.automation_id ?? raw.automationId ?? ''),
    status: mapExecutionStatusFromBackend(raw.status),
    startedAt,
    queuedAt,
    completedAt,
    parameters:
      (raw.trigger_payload as Record<string, unknown>) ??
      (raw.parameters as Record<string, unknown>) ??
      {},
    steps,
    totalCost,
  }
}

/**
 * Map a raw backend execution list response.  Preserves pagination metadata
 * verbatim (``has_more``, ``next_cursor`` and ``object``) and runs each row
 * through ``mapExecutionFromBackend``.
 *
 * ``next_cursor`` is the opaque token the backend wants echoed back as
 * ``?cursor=`` on the next page.  It is only present when ``has_more`` is true.
 */
function mapExecutionListFromBackend(raw: {
  data?: BackendExecution[]
  has_more?: boolean
  next_cursor?: string
  object?: 'list'
}): ExecutionListResponse {
  return {
    data: (raw.data ?? []).map(mapExecutionFromBackend),
    has_more: raw.has_more ?? false,
    next_cursor: raw.next_cursor,
    object: 'list',
  }
}

/**
 * Every recognised key on AutomationUpdateRequest. Used only for the dev-mode
 * unrecognised-key warning in mapUpdateToBackend.
 */
const UPDATE_REQUEST_KEYS: ReadonlySet<string> = new Set<keyof AutomationUpdateRequest>([
  'name',
  'description',
  'status',
  'trigger',
  'steps',
  'inputSchema',
  'deprecated',
  'deprecatedReason',
  'summariserHint',
  'summariseExecution',
  'conditions',
  'priority',
  'triggerEventType',
  'triggerSchedule',
  'triggerSource',
  'entryStepId',
])

/**
 * Maps a frontend update request to the backend's expected PATCH body.
 *
 * Frontend → Backend field mapping:
 *   status: 'active'   → enabled: true
 *   status: 'paused'   → enabled: false
 *   status: 'deprecated' → deprecated: true (handled separately via deprecateAutomation)
 *   trigger.type/config → trigger_event_type / trigger_schedule / trigger_source (flat)
 *
 * All input properties are camelCase; the emitted body is snake_case. Keys that
 * are not recognised here are silently dropped from the PATCH body, which the
 * backend then treats as "leave unchanged" — see the dev-mode warning below.
 */
export function mapUpdateToBackend(
  data: AutomationUpdateRequest
): Record<string, unknown> {
  const body: Record<string, unknown> = {}

  // Guard against snake_case (or otherwise misspelled) keys silently vanishing:
  // every branch below is an `undefined` check on a camelCase property, so an
  // unrecognised key is dropped and the backend's `omitempty` update semantics
  // turn the save into a 200 OK no-op. Warn loudly in dev; never throw, so a
  // stray key can't break a production save.
  if (import.meta.env.DEV) {
    const unknownKeys = Object.keys(data).filter(
      (key) => !UPDATE_REQUEST_KEYS.has(key)
    )
    if (unknownKeys.length > 0) {
      console.warn(
        `[automations] mapUpdateToBackend: ignoring unrecognised key(s) ${unknownKeys.join(', ')}. ` +
          'Update payloads must use the camelCase AutomationUpdateRequest shape.'
      )
    }
  }

  if (data.name !== undefined) {
    body.name = data.name
  }
  if (data.description !== undefined) {
    body.description = data.description
  }

  // Convert status → enabled boolean (deprecated is handled separately).
  if (data.status !== undefined && data.status !== 'deprecated') {
    body.enabled = data.status === 'active'
  }

  // Conditions
  if (data.conditions !== undefined) {
    body.conditions = data.conditions
  }
  // Priority
  if (data.priority !== undefined) {
    body.priority = data.priority
  }
  // Trigger fields (flat backend shape)
  if (data.triggerEventType !== undefined) {
    body.trigger_event_type = data.triggerEventType
  }
  if (data.triggerSchedule !== undefined) {
    body.trigger_schedule = data.triggerSchedule
  }
  if (data.triggerSource !== undefined) {
    body.trigger_source = data.triggerSource
  }
  if (data.entryStepId !== undefined) {
    body.entry_step_id = data.entryStepId
  }
  // Steps pass-through
  if (data.steps !== undefined) {
    body.steps = data.steps
  }
  // Summariser
  if (data.summariseExecution !== undefined) {
    body.summarise_execution = data.summariseExecution
  }
  if (data.summariserHint !== undefined) {
    body.summariser_hint = data.summariserHint
  }
  // Deprecation
  if (data.deprecated !== undefined) {
    body.deprecated = data.deprecated
  }
  if (data.deprecatedReason !== undefined) {
    body.deprecated_reason = data.deprecatedReason
  }

  return body
}

// ── List Automations ────────────────────────────────────────────────────────

export interface ListAutomationsParams {
  filters?: AutomationFilters
  sort?: AutomationSortState
  limit?: number
  cursor?: string
}

/**
 * List automations with optional filtering and pagination.
 */
export async function listAutomations(
  params: ListAutomationsParams = {}
): Promise<AutomationListResponse> {
  const queryParams: Record<string, string | number | boolean> = {}

  // Pagination
  if (params.limit) queryParams.limit = params.limit
  if (params.cursor) queryParams.starting_after = params.cursor

  // Search
  if (params.filters?.q) {
    queryParams.q = params.filters.q
  }

  // Trigger type filter
  if (params.filters?.trigger?.length) {
    queryParams.trigger = params.filters.trigger.join(',')
  }

  // Status filter — map frontend status values to backend enabled filter.
  // The backend supports ?enabled=true/false, not ?status=active/paused.
  if (params.filters?.status?.length) {
    const statuses = params.filters.status
    if (statuses.includes('active') && !statuses.includes('paused')) {
      queryParams.enabled = true
    } else if (statuses.includes('paused') && !statuses.includes('active')) {
      queryParams.enabled = false
    }
    // If both or neither, don't filter on enabled.
  }

  // Date range filters
  if (params.filters?.last_from) {
    queryParams.last_from = params.filters.last_from
  }
  if (params.filters?.last_to) {
    queryParams.last_to = params.filters.last_to
  }

  // Sorting
  if (params.sort?.column) {
    queryParams.sort = params.sort.column
    if (params.sort.direction) {
      queryParams.order = params.sort.direction
    }
  }

  const raw = await api.get<{ data: BackendAutomation[]; has_more: boolean; object: string }>(
    PATHS.automations,
    queryParams
  )

  return {
    data: (raw.data ?? []).map(mapAutomationFromBackend),
    has_more: raw.has_more ?? false,
    object: 'list',
  }
}

// ── Get Automation ──────────────────────────────────────────────────────────

/**
 * Get a single automation by ID.
 * Returns automation with trigger config, step graph, and recent runs.
 */
export async function getAutomation(id: string): Promise<Automation> {
  const raw = await api.get<BackendAutomation>(PATHS.automation(id))
  return mapAutomationFromBackend(raw)
}

// ── Create Automation ───────────────────────────────────────────────────────

/**
 * Create a new automation.
 */
export async function createAutomation(data: CreateAutomationRequest): Promise<Automation> {
  const raw = await api.post<BackendAutomation>(PATHS.automations, data)
  return mapAutomationFromBackend(raw)
}

// ── Update Automation ───────────────────────────────────────────────────────

/**
 * Update an automation.
 * Used for inline edits (name, status toggle) and drawer edits (trigger, steps).
 * Maps frontend status values to backend enabled booleans.
 */
export async function updateAutomation(
  id: string,
  data: AutomationUpdateRequest
): Promise<Automation> {
  const backendBody = mapUpdateToBackend(data)
  const raw = await api.patch<BackendAutomation>(PATHS.automation(id), backendBody)
  return mapAutomationFromBackend(raw)
}

// ── Deprecate / Restore Automation ─────────────────────────────────────────

/**
 * Deprecate an automation permanently.
 * The service layer atomically sets enabled=false and records deprecated_at.
 */
export async function deprecateAutomation(id: string, reason?: string): Promise<Automation> {
  const body: Record<string, unknown> = { deprecated: true }
  if (reason) body.deprecated_reason = reason
  const raw = await api.patch<BackendAutomation>(PATHS.automation(id), body)
  return mapAutomationFromBackend(raw)
}

/**
 * Restore a deprecated automation.
 * Clears deprecated_at and deprecated_reason; does NOT automatically re-enable.
 * The operator must explicitly resume after restoring.
 */
export async function restoreAutomation(id: string): Promise<Automation> {
  const raw = await api.patch<BackendAutomation>(PATHS.automation(id), { deprecated: false })
  return mapAutomationFromBackend(raw)
}

// ── Delete Automation ───────────────────────────────────────────────────────

/**
 * Delete an automation.
 */
export async function deleteAutomation(id: string): Promise<void> {
  await api.delete(PATHS.automation(id))
}

// ── Run Automation ──────────────────────────────────────────────────────────

/**
 * Execute an automation with provided parameters.
 * Returns execution_id for tracking.
 */
export async function runAutomation(
  id: string,
  parameters: Record<string, unknown>
): Promise<RunAutomationResponse> {
  return api.post<RunAutomationResponse>(PATHS.execute(id), { parameters })
}

// ── List Automation Executions ──────────────────────────────────────────────

export interface ListAutomationExecutionsParams {
  limit?: number
  cursor?: string
}

/**
 * List executions for a specific automation.
 */
export async function listAutomationExecutions(
  automationId: string,
  params: ListAutomationExecutionsParams = {}
): Promise<ExecutionListResponse> {
  const queryParams: Record<string, string | number> = {}

  if (params.limit) queryParams.limit = params.limit
  // The operations backend reads the pagination cursor from ``?cursor=`` (not
  // ``?starting_after=``); see ``HandleListAutomationExecutions`` in
  // services/operations/backend/internal/handlers/http/automation_handler.go.
  // Pass the value returned in ``next_cursor`` verbatim — it is an opaque
  // backend-encoded token.
  if (params.cursor) queryParams.cursor = params.cursor

  const raw = await api.get<{
    data?: BackendExecution[]
    has_more?: boolean
    next_cursor?: string
    object?: 'list'
  }>(PATHS.automationExecutions(automationId), queryParams)
  return mapExecutionListFromBackend(raw)
}

// ── List All Executions ─────────────────────────────────────────────────────

export interface ListExecutionsParams {
  filters?: ExecutionFilters
  limit?: number
  cursor?: string
}

/**
 * List all executions across automations.
 */
export async function listExecutions(
  params: ListExecutionsParams = {}
): Promise<ExecutionListResponse> {
  const queryParams: Record<string, string | number> = {}

  if (params.limit) queryParams.limit = params.limit
  // The operations backend reads the pagination cursor from ``?cursor=`` (not
  // ``?starting_after=``); see ``HandleListExecutions`` in
  // services/operations/backend/internal/handlers/http/automation_execution_handler.go.
  // Pass the value returned in ``next_cursor`` verbatim — it is an opaque
  // backend-encoded token (currently the ``Created`` timestamp of the last
  // row on the previous page, but treat it as opaque).
  if (params.cursor) queryParams.cursor = params.cursor

  if (params.filters?.automationId) {
    queryParams.automation_id = params.filters.automationId
  }
  if (params.filters?.status?.length) {
    queryParams.status = params.filters.status.join(',')
  }
  if (params.filters?.from) {
    queryParams.from = params.filters.from
  }
  if (params.filters?.to) {
    queryParams.to = params.filters.to
  }

  const raw = await api.get<{
    data?: BackendExecution[]
    has_more?: boolean
    next_cursor?: string
    object?: 'list'
  }>(PATHS.executions, queryParams)
  return mapExecutionListFromBackend(raw)
}

// ── Get Executions by Facet ─────────────────────────────────────────────────

/**
 * List executions that match an arbitrary facet key/value pair.
 *
 * The operations service indexes arbitrary key/value pairs on each execution
 * (e.g. ``facet_interaction_id=<id>``). This is a shared helper that lets
 * any feature look up executions by a stored facet without duplicating the
 * raw ``/v1/executions?{facetKey}={facetValue}`` query logic.
 *
 * Returns mapped ``Execution[]`` from the first page (up to ``limit``).
 * Returns an empty array on network/parse errors so callers can treat a
 * missing result gracefully without surfacing an exception.
 *
 * Task 4054c.
 */
export async function getExecutionsByFacet(
  facetKey: string,
  facetValue: string,
  limit = 5,
): Promise<Execution[]> {
  try {
    const raw = await api.get<{
      data?: BackendExecution[]
      has_more?: boolean
      next_cursor?: string
      object?: 'list'
    }>(PATHS.executions, { [facetKey]: facetValue, limit })
    return mapExecutionListFromBackend(raw).data
  } catch {
    return []
  }
}

// ── Get Execution ───────────────────────────────────────────────────────────

/**
 * Get execution detail with step log.
 * Used for polling during execution streaming.
 */
export async function getExecution(executionId: string): Promise<Execution> {
  const raw = await api.get<BackendExecution>(PATHS.execution(executionId))
  return mapExecutionFromBackend(raw)
}

// ── Abort Execution ─────────────────────────────────────────────────────────

/**
 * Abort a running execution.
 */
export async function abortExecution(executionId: string): Promise<Execution> {
  const raw = await api.patch<BackendExecution>(PATHS.execution(executionId), {
    status: 'aborted',
  })
  return mapExecutionFromBackend(raw)
}

// ── Retry Execution ─────────────────────────────────────────────────────────

/**
 * Retry an execution from the beginning.
 *
 * The backend accepts retry for `running`, `failed`, and
 * `pending_operator_review` executions and requires `reviewer_id` in
 * the request body — when an execution is in `pending_operator_review`
 * the retry resolves the original execution with `resolution=retried`
 * (attributed to the reviewer) and creates a fresh execution from the
 * trigger. Omitting `reviewer_id` results in
 * `HTTP 400 { type: "validation_error", message: "reviewer_id is required" }`.
 *
 * Returns the new execution id created by the retry.
 */
export async function retryExecution(
  executionId: string,
  reviewerId: string,
): Promise<RunAutomationResponse> {
  return api.post<RunAutomationResponse>(PATHS.executionRetry(executionId), {
    reviewer_id: reviewerId,
  })
}

// ── Approve Execution (HITL) ────────────────────────────────────────────────

/**
 * Approve an execution waiting for approval.
 */
export async function approveExecution(executionId: string): Promise<Execution> {
  const raw = await api.post<BackendExecution>(PATHS.executionApprove(executionId), {})
  return mapExecutionFromBackend(raw)
}

// ── Reject Execution (HITL) ─────────────────────────────────────────────────

/**
 * Reject an execution waiting for approval.
 */
export async function rejectExecution(
  executionId: string,
  reason?: string
): Promise<Execution> {
  const raw = await api.post<BackendExecution>(PATHS.executionReject(executionId), { reason })
  return mapExecutionFromBackend(raw)
}

// ── Suppress Execution (operator dismissal of a stuck awaiting-approval run) ──

/**
 * Suppress an execution that is stuck awaiting an operator decision.
 *
 * Sends ``resolution: "suppressed"`` to ``POST /v1/executions/{id}/reject`` —
 * the same terminal path the operator queue's "dismiss" uses. This cancels the
 * run, releases any suspended agent session, and (for ``tool_approval`` items)
 * clears the agent-side pending attention.
 *
 * The primary use case is clearing runs whose canonical attention item never
 * reached the queue (e.g. a transient registration failure), leaving the run
 * "active" and invisible — which also blocks re-dispatch via the dispatch
 * idempotency guard (409 already_active).
 *
 * The backend requires ``reviewer_id`` (the acting operator's consumer_id) and
 * returns ``HTTP 400 { type: "validation_error" }`` when it is omitted.
 */
export async function suppressExecution(
  executionId: string,
  reviewerId: string,
  reason?: string
): Promise<Execution> {
  const raw = await api.post<BackendExecution>(PATHS.executionReject(executionId), {
    reviewer_id: reviewerId,
    resolution: 'suppressed',
    reason: reason ?? 'Suppressed from Stream Runs (stuck awaiting-approval run).',
  })
  return mapExecutionFromBackend(raw)
}

// ── Batch Fetch for Detail View ─────────────────────────────────────────────

export interface AutomationDetailBatch {
  automation: Automation | null
  executions: Execution[]
  errors: Record<string, string>
}

/**
 * Fetch automation with recent executions in parallel.
 * Used for inspector detail view.
 */
export async function fetchAutomationDetail(
  id: string
): Promise<AutomationDetailBatch> {
  const errors: Record<string, string> = {}

  const results = await Promise.allSettled([
    getAutomation(id),
    listAutomationExecutions(id, { limit: 50 }),
  ])

  const getValue = <T>(
    result: PromiseSettledResult<T>,
    key: string
  ): T | null => {
    if (result.status === 'fulfilled') {
      return result.value
    }
    errors[key] =
      result.reason instanceof Error
        ? result.reason.message
        : String(result.reason)
    return null
  }

  const automation = getValue(results[0], 'automation')
  const executionsResponse = getValue(results[1], 'executions')

  return {
    automation,
    executions: executionsResponse?.data ?? [],
    errors,
  }
}

// ── Poll Execution Helper ───────────────────────────────────────────────────

/**
 * Create an execution poller that calls a callback on each update.
 * Returns a cleanup function to stop polling.
 *
 * Polling interval: 1 second (per task spec S7.6)
 * Stops when execution reaches a terminal status.
 */
export function createExecutionPoller(
  executionId: string,
  onUpdate: (execution: Execution) => void,
  onError: (error: Error) => void,
  onComplete?: () => void
): () => void {
  let intervalId: number | null = null
  let isActive = true

  const poll = async () => {
    if (!isActive) return

    try {
      const execution = await getExecution(executionId)
      onUpdate(execution)

      // Check for terminal status
      const terminalStatuses = ['succeeded', 'failed', 'aborted']
      if (terminalStatuses.includes(execution.status)) {
        if (intervalId !== null) {
          clearInterval(intervalId)
          intervalId = null
        }
        onComplete?.()
      }
    } catch (error) {
      onError(error instanceof Error ? error : new Error(String(error)))
    }
  }

  // Initial poll
  poll()

  // Start interval
  intervalId = window.setInterval(poll, 1000)

  // Return cleanup function
  return () => {
    isActive = false
    if (intervalId !== null) {
      clearInterval(intervalId)
      intervalId = null
    }
  }
}

// ── Utility Functions ───────────────────────────────────────────────────────

/**
 * Calculate total cost from execution steps.
 */
export function calculateExecutionCost(execution: Execution): number {
  return (
    execution.totalCost ??
    execution.steps.reduce((sum, step) => sum + (step.cost ?? 0), 0)
  )
}

/**
 * Calculate total duration from execution.
 */
export function calculateExecutionDuration(execution: Execution): number | null {
  if (!execution.startedAt) return null
  const endTime = execution.completedAt
    ? new Date(execution.completedAt).getTime()
    : Date.now()
  return endTime - new Date(execution.startedAt).getTime()
}

/**
 * Check if execution is in a terminal state.
 */
export function isExecutionTerminal(execution: Execution): boolean {
  return ['succeeded', 'failed', 'aborted'].includes(execution.status)
}

/**
 * Check if execution can be aborted.
 */
export function canAbortExecution(execution: Execution): boolean {
  return execution.status === 'running' || execution.status === 'pending'
}
